import * as Vue from 'vue';
import { inject, computed, onBeforeUnmount, onMounted, onUpdated, reactive, ref } from 'vue';
import { ContextHolder, FronteggFrameworks } from '@frontegg/rest-api';
export { ContextHolder } from '@frontegg/rest-api';
import set from 'set-value';
import { isAuthRoute, authInitialState, loginActions, socialLoginsActions, activateAccountActions, acceptInvitationActions, forgotPasswordActions, signUpActions, profileActions, ssoActions, mfaActions, teamActions, apiTokensActions, securityPolicyActions, tenantsActions, bindActionCreators, authActions, getEntitlements } from '@frontegg/redux-store';
import get from 'get-value';
import { initialize, AdminPortal } from '@frontegg/js';
export { AdminPortal } from '@frontegg/js';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const FRONTEGG_STORE_KEY = 'FRONTEGG_STORE_KEY';
const FRONTEGG_REDUX_BINDINGS = 'FRONTEGG_REDUX_BINDINGS';
const FRONTEGG_UNSUBSCRIBE = 'FRONTEGG_UNSUBSCRIBE';
const FRONTEGG_LOADER_UNSUBSCRIBE = 'FRONTEGG_LOADER_UNSUBSCRIBE';
const fronteggLoadedKey = Symbol('fronteggLoade');
const authStateKey = Symbol('authState');
const unsubscribeFronteggStoreKey = Symbol('unsubscribeFronteggStore');
const fronteggAuthKey = Symbol('fronteggAuth');
const routerKey = Symbol('router');
const fronteggOptionsKey = Symbol('fronteggOptions');
const fronteggStoreKey = Symbol('fronteggStore');

const setStoreKey = (instance, store) => {
  var _a;

  return instance[FRONTEGG_STORE_KEY] = (_a = instance[FRONTEGG_STORE_KEY]) !== null && _a !== void 0 ? _a : store;
};
const getStore = instance => instance[FRONTEGG_STORE_KEY];
const getStoreBinding = instance => instance[FRONTEGG_REDUX_BINDINGS];
const setStoreBinding = (instance, data) => instance[FRONTEGG_REDUX_BINDINGS] = data;
const setStoreUnsubscribe = (instance, unsubscribe) => instance[FRONTEGG_UNSUBSCRIBE] = unsubscribe;
const storeUnsubscribe = instance => {
  var _a;

  return (_a = instance[FRONTEGG_UNSUBSCRIBE]) === null || _a === void 0 ? void 0 : _a.call(instance);
}; // export const setMapState = (instance: any) => {instance.mapState = (...props: any[]) => mapState(instance, ...props);};

const setLoadingUnsubscribe = (instance, unsubscribe) => instance[FRONTEGG_LOADER_UNSUBSCRIBE] = unsubscribe;
const loadingUnsubscribe = instance => {
  var _a;

  return (_a = instance[FRONTEGG_LOADER_UNSUBSCRIBE]) === null || _a === void 0 ? void 0 : _a.call(instance);
};
const registerFronteggPlugin = (Vue, plugin) => {
  var _a;

  Vue.fronteggPlugins = (_a = Vue.fronteggPlugins) !== null && _a !== void 0 ? _a : [];
  Vue.fronteggPlugins.push(plugin);
};

class StoreHolder {
  static getInstance() {
    if (!StoreHolder.instance) {
      StoreHolder.instance = new StoreHolder();
    }

    return StoreHolder.instance;
  }

  static setStore(store) {
    StoreHolder.getInstance().store = store;
  }

  static getStore() {
    return StoreHolder.getInstance().store;
  }

  static setBasename(baseName) {
    StoreHolder.getInstance().baseName = baseName;
  }

  static getBasename() {
    return StoreHolder.getInstance().baseName;
  }

}

const setupOnRedirectTo = (router, routes) => {
  const baseName = router.options.base || '';
  StoreHolder.setBasename(baseName);

  const onRedirectTo = (_path, opts) => {
    let path = _path;

    if (path.startsWith(baseName) && baseName !== '/') {
      path = path.substring(baseName.length - 1);
    }

    if ((opts === null || opts === void 0 ? void 0 : opts.preserveQueryParams) || isAuthRoute(path, routes)) {
      path = `${path}${window.location.search}`;
    }

    if (opts === null || opts === void 0 ? void 0 : opts.refresh) {
      window.location.href = path;
    } else {
      if (opts === null || opts === void 0 ? void 0 : opts.replace) {
        router.replace(path);
      } else {
        router.push(path);
      }
    }
  };

  ContextHolder.setOnRedirectTo(onRedirectTo);
  return onRedirectTo;
};
const syncStateWithComponent = (component, bindings) => () => {
  const state = getStore(component).getState();
  Object.keys(bindings).forEach(subState => {
    Object.keys(bindings[subState]).forEach(prop => {
      const getter = bindings[subState][prop];

      if (!getter) {
        return;
      }

      const newData = getter(get(state, subState));
      const vueObject = Object.assign({}, Vue);
      let version;
      if (vueObject.default) version = 2;

      if (vueObject.version && vueObject.version[0] === '3') {
        version = 3;
      }

      if (version === 3) {
        set(component.$data, prop, newData);
      }

      if (version === 2) {
        set(component._data, prop, newData);
      }
    });
  });
};
const defaultGetterGenerator = prop => state => get(state, prop);

const bindOrFallback = (vueInstance, value, fallbackGetter) => {
  return typeof value === 'function' ? value.bind(vueInstance) : fallbackGetter(value);
};

const directCallOrFallback = (vueInstance, value, fallbackGetter, state) => {
  return typeof value === 'function' ? value.call(vueInstance, state) : fallbackGetter(value)(state);
};

const generateStoreBindingBySlices = (vueInstance, subState, slices, obj, fallbackGetter) => {
  return slices.reduce((result, prop) => Object.assign({}, result, {
    [subState]: Object.assign(Object.assign({}, result[subState]), {
      [prop]: bindOrFallback(vueInstance, obj[prop], fallbackGetter)
    })
  }), getStoreBinding(vueInstance) || {});
};
const objectMappers = (subState, obj, fallbackGetter) => function () {
  // @ts-ignore
  const vueInstance = this;
  const slices = Object.keys(obj);
  const state = get(getStore(vueInstance).getState(), subState);
  const calculatedBinding = generateStoreBindingBySlices(vueInstance, subState, slices, obj, fallbackGetter);
  setStoreBinding(vueInstance, calculatedBinding);
  return slices.reduce((result, prop) => Object.assign({}, result, {
    [prop]: directCallOrFallback(vueInstance, obj[prop], fallbackGetter, state)
  }), {});
};

class ActionsHolder {
  static getInstance() {
    if (!ActionsHolder.instance) {
      ActionsHolder.instance = new ActionsHolder();
    }

    return ActionsHolder.instance;
  }

  static setActions(actions) {
    ActionsHolder.getInstance().actions = actions;
  }

  static getAction(action) {
    return ActionsHolder.getInstance().actions[action];
  }

}

class FronteggAuthService {
  constructor(_a) {
    var {
      router
    } = _a,
        options = __rest(_a, ["router"]);

    this.state = authInitialState;
    this.userRef = undefined;

    this.init = (options, store) => {
      this.store = store;
      Object.entries({
        loginActions,
        socialLoginsActions,
        activateAccountActions,
        acceptInvitationActions,
        forgotPasswordActions,
        signUpActions,
        profileActions,
        ssoActions,
        mfaActions,
        teamActions,
        apiTokensActions,
        securityPolicyActions,
        tenantsActions
      }).forEach(([key, actions]) => {
        Object.assign(this, {
          [key]: bindActionCreators(actions, this.store.dispatch)
        });
      });
      const detachableActions = bindActionCreators(authActions, this.store.dispatch);
      ActionsHolder.setActions(detachableActions);

      if (!options.hostedLoginBox) {
        this.store.subscribe(this.storeSubscriber);
      }
    };

    this.storeSubscriber = () => {
      this.state = this.store.getState().auth;
      let notifyChange = false;

      if (this.state.user !== this.userRef) {
        this.userRef = this.state.user;
        notifyChange = true;
      }

      if (this.state.isAuthenticated !== this.isAuthenticatedRef) {
        this.isAuthenticatedRef = this.state.isAuthenticated;
        notifyChange = true;
      }

      if (!this.userRef || !this.state.isAuthenticated) {
        clearInterval(this.accessTokenUpdaterRef);
      } else if (notifyChange) {
        this.accessTokenUpdater();
      }
    };

    this.accessTokenUpdater = (firstTime = false) => {
      var _a;

      this.accessTokenUpdaterRef && clearInterval(this.accessTokenUpdaterRef);

      if (firstTime) {
        this.loginActions.requestAuthorize(true);
      } else {
        if (this.isAuthenticated) {
          const ttl = (((_a = this.user) === null || _a === void 0 ? void 0 : _a.expiresIn) || 20) * 1000 * 0.8;
          this.accessTokenUpdaterRef = setInterval(this.loginActions.requestAuthorize, ttl);
        }
      }
    };

    this._routes = Object.assign(Object.assign({}, authInitialState.routes), options.routes);
    this.router = router;
  }

  get loading() {
    var _a, _b;

    return (_b = (_a = this.store) === null || _a === void 0 ? void 0 : _a.getState().auth.isLoading) !== null && _b !== void 0 ? _b : true;
  }

  get routes() {
    var _a, _b;

    return (_b = (_a = this.store) === null || _a === void 0 ? void 0 : _a.getState().auth.routes) !== null && _b !== void 0 ? _b : this._routes;
  }

  get isAuthenticated() {
    var _a, _b;

    return (_b = (_a = this.store) === null || _a === void 0 ? void 0 : _a.getState().auth.isAuthenticated) !== null && _b !== void 0 ? _b : false;
  }

  get user() {
    var _a;

    return (_a = this.store) === null || _a === void 0 ? void 0 : _a.getState().auth.user;
  }

}

// @ts-ignore

const mapSubState = (statePrefix, propertyName) => function () {
  const obj = {
    [propertyName !== null && propertyName !== void 0 ? propertyName : statePrefix.substring('auth.'.length)]: state => state
  }; // @ts-ignore

  return objectMappers(statePrefix, obj, defaultGetterGenerator).bind(this)();
};

const mapAuthState = _this => mapSubState('auth', 'authState').bind(_this);
const mapEntitlementsState = _this => mapSubState('auth.entitlementsState.entitlements', 'entitlements').bind(_this);
const mapLoginState = _this => mapSubState('auth.loginState').bind(_this);
const mapAcceptInvitationState = _this => mapSubState('auth.acceptInvitationState').bind(_this);
const mapActivateAccountState = _this => mapSubState('auth.activateState').bind(_this);
const mapApiTokensState = _this => mapSubState('auth.apiTokensState').bind(_this);
const mapForgotPasswordState = _this => mapSubState('auth.forgotPasswordState').bind(_this);
const mapMfaState = _this => mapSubState('auth.mfaState').bind(_this);
const mapProfileState = _this => mapSubState('auth.profileState').bind(_this);
const mapSecurityPolicyState = _this => mapSubState('auth.securityPolicyState').bind(_this);
const mapSignUpState = _this => mapSubState('auth.signUpState').bind(_this);
const mapSocialLoginState = _this => mapSubState('auth.socialLoginState').bind(_this);
const mapSsoState = _this => mapSubState('auth.ssoState').bind(_this);
const mapTeamState = _this => mapSubState('auth.teamState').bind(_this);
const mapTenantsState = _this => mapSubState('auth.tenantsState').bind(_this);
const connectMapState = _this => {
  Object.assign(_this, {
    mapAuthState: mapAuthState(_this),
    mapEntitlementsState: mapEntitlementsState(_this),
    mapLoginState: mapLoginState(_this),
    mapAcceptInvitationState: mapAcceptInvitationState(_this),
    mapActivateAccountState: mapActivateAccountState(_this),
    mapApiTokensState: mapApiTokensState(_this),
    mapForgotPasswordState: mapForgotPasswordState(_this),
    mapMfaState: mapMfaState(_this),
    mapProfileState: mapProfileState(_this),
    mapSecurityPolicyState: mapSecurityPolicyState(_this),
    mapSignUpState: mapSignUpState(_this),
    mapSocialLoginState: mapSocialLoginState(_this),
    mapSsoState: mapSsoState(_this),
    mapTeamState: mapTeamState(_this),
    mapTenantsState: mapTenantsState(_this)
  });
};

const actionGetter = action => (...args) => ActionsHolder.getAction(action)(...args);

const mapAuthActions = action => actionGetter(action);
const mapLoginActions = action => {
  if (action === 'loginWithRedirect') {
    return actionGetter('requestHostedLoginAuthorize');
  }

  return actionGetter(action);
};
const mapAcceptInvitationActions = action => actionGetter(action);
const mapActivateAccountActions = action => actionGetter(action);
const mapApiTokensActions = action => actionGetter(action);
const mapForgotPasswordActions = action => actionGetter(action);
const mapMfaActions = action => actionGetter(action);
const mapProfileActions = action => actionGetter(action);
const mapSecurityPolicyActions = action => actionGetter(action);
const mapSignupActions = action => actionGetter(action);
const mapSocialLoginActions = action => actionGetter(action);
const mapSsoActions = action => actionGetter(action);
const mapTeamActions = action => actionGetter(action);
const connectFronteggStoreV3 = store => {
  const initialState = store.getState();
  const authState = reactive(Object.assign({}, initialState.auth));
  const unsubscribe = store.subscribe(() => {
    const state = store.getState().auth;
    Object.entries(state).forEach(([key, value]) => {
      if (authState[key] !== state[key]) {
        authState[key] = value;
      }
    });
  });
  return {
    authState,
    unsubscribe
  };
};
const useFronteggLoaded = () => {
  const fronteggLoaded = inject(fronteggLoadedKey);
  return fronteggLoaded;
};
/**
  @param keys The requested entitlement keys
  @returns Entitlements contain state data for every key (inc if entitled)
*/

const useEntitlements = keys => {
  const authState = inject(authStateKey);
  return computed(() => {
    var _a;

    return getEntitlements(((_a = authState.entitlementsState) === null || _a === void 0 ? void 0 : _a.entitlements) || {}, keys).reduce((entitlementsResult, {
      isEntitled
    }, i) => Object.assign(Object.assign({}, entitlementsResult), {
      [keys[i]]: {
        isEntitled
      }
    }), {});
  });
};
const useUnsubscribeFronteggStore = () => {
  const unsubscribeFronteggStore = inject(unsubscribeFronteggStoreKey);
  return unsubscribeFronteggStore;
};
const useAuthState = () => {
  const authState = inject(authStateKey);
  return authState;
};
const useFronteggStore = () => {
  const fronteggStore = inject(fronteggStoreKey);
  return fronteggStore;
};
const useFronteggAuth = () => {
  const fronteggAuth = inject(fronteggAuthKey);
  return fronteggAuth;
};
const useFrontegg = () => {
  const fronteggLoaded = useFronteggLoaded();
  const unsubscribeFronteggStore = useUnsubscribeFronteggStore();
  const authState = useAuthState();
  const fronteggAuth = useFronteggAuth();
  const fronteggStore = useFronteggStore();

  const loginWithRedirect = () => {
    var _a; // @ts-ignore


    if (!((_a = fronteggAuth.router) === null || _a === void 0 ? void 0 : _a.currentRoute.path.startsWith(authState.routes.hostedLoginRedirectUrl))) {
      fronteggStore.dispatch({
        type: 'auth/setState',
        payload: {
          isLoading: true
        }
      });
      fronteggAuth.loginActions.requestHostedLoginAuthorize();
    }
  };

  onBeforeUnmount(() => {
    unsubscribeFronteggStore();
  });
  return {
    fronteggLoaded,
    authState,
    fronteggAuth,
    loginWithRedirect
  };
};
const useFronteggAuthGuard = () => {
  const fronteggAuth = useFronteggAuth();
  const fronteggOptions = inject(fronteggOptionsKey);
  const authState = useAuthState();
  const router = inject(routerKey);
  const fronteggStore = useFronteggStore();

  const isAuthRoutes = path => {
    const {
      routes
    } = authState;
    return Object.values(routes).filter(path => path !== routes.authenticatedUrl).includes(path);
  };

  const checkGuard = () => {
    var _a;

    if (!isAuthRoutes((_a = fronteggAuth.router) === null || _a === void 0 ? void 0 : _a.currentRoute.path) && !authState.isAuthenticated && !authState.isLoading) {
      if (fronteggOptions.hostedLoginBox) {
        fronteggStore.dispatch({
          type: 'auth/setState',
          payload: {
            isLoading: true
          }
        });
        fronteggAuth.loginActions.requestHostedLoginAuthorize();
      } else {
        router.push(authState.routes.loginUrl);
      }
    }
  };

  onMounted(checkGuard);
  onUpdated(checkGuard);
};

var sdkVersion = {
  version: '2.0.40'
};

const isAuthenticatedGuard = function (_to, _from, next) {
  var _a, _b;

  const checkIfAuthenticated = () => {
    var _a;

    const state = StoreHolder.getStore().getState().auth;

    if (state.isAuthenticated) {
      next();
    } else {
      const basename = (_a = StoreHolder.getBasename()) !== null && _a !== void 0 ? _a : '';
      const calculatedBasename = basename.endsWith('/') ? basename.substring(0, basename.length - 1) : '';
      localStorage.setItem('FRONTEGG_AFTER_AUTH_REDIRECT_URL', `${calculatedBasename}${_to.fullPath}`);
      next({
        path: state.routes.loginUrl,
        replace: true
      });
    }
  };

  let store = (_b = (_a = StoreHolder.getStore()) === null || _a === void 0 ? void 0 : _a.getState()) === null || _b === void 0 ? void 0 : _b.auth;

  if (store && !(store === null || store === void 0 ? void 0 : store.isLoading)) {
    checkIfAuthenticated();
    return;
  }

  const waitFor = setInterval(() => {
    var _a, _b;

    store = (_b = (_a = StoreHolder.getStore()) === null || _a === void 0 ? void 0 : _a.getState()) === null || _b === void 0 ? void 0 : _b.auth;

    if (store && !(store === null || store === void 0 ? void 0 : store.isLoading)) {
      clearInterval(waitFor);
      checkIfAuthenticated();
    }
  }, 1);
};

let fronteggApp = null;
const Frontegg = {
  install(Vue, options) {
    var _a, _b;

    const _c = options !== null && options !== void 0 ? options : {},
          {
      router
    } = _c,
          rest = __rest(_c, ["router"]);

    const {
      contextOptions
    } = options !== null && options !== void 0 ? options : {};

    if (contextOptions == null) {
      throw Error('contextOptions must be passed to Vue.use(Frontegg, { /* OPTIONS */ })');
    }

    contextOptions.requestCredentials = (_a = contextOptions.requestCredentials) !== null && _a !== void 0 ? _a : 'include';
    contextOptions.metadataHeaders = {
      framework: FronteggFrameworks.Vuejs,
      fronteggSdkVersion: `@frontegg/vuejs@${sdkVersion.version}`
    };
    ContextHolder.setContext(contextOptions);
    let pluginRegistered = false; // frontegg loader subscription

    let fronteggLoaded = false;
    const isVue3 = Vue.version.charAt(0) === '3';
    let fronteggLoadedV3;

    if (isVue3) {
      fronteggLoadedV3 = ref(false);
    }

    const fronteggLoadedSubscribes = new Set();

    const fronteggLoadedSubscribe = instance => {
      const func = () => {
        instance.fronteggLoaded = fronteggLoaded;
      };

      fronteggLoadedSubscribes.add(func);
      return () => fronteggLoadedSubscribes.delete(func);
    };

    const onRedirectTo = router && setupOnRedirectTo(router, (_b = options === null || options === void 0 ? void 0 : options.authOptions) === null || _b === void 0 ? void 0 : _b.routes);
    fronteggApp = initialize(Object.assign(Object.assign({}, rest), {
      onRedirectTo,
      basename: router === null || router === void 0 ? void 0 : router.options.base
    }));
    const store = fronteggApp.store;
    StoreHolder.setStore(store); // @ts-ignore

    Vue.$fronteggApp = fronteggApp;

    if (!Vue.fronteggAuth) {
      Vue.fronteggAuth = new FronteggAuthService({
        router
      });
      registerFronteggPlugin(Vue, Vue.fronteggAuth);
    }

    const registerPlugins = instance => {
      pluginRegistered = true;
      (Vue.fronteggPlugins || []).forEach(plugin => plugin.init(rest, store));
      setStoreKey(instance, store);
      instance.fronteggAuth = Vue.fronteggAuth;
      connectMapState(instance);
    };

    const checkIfPluginsLoaded = () => {
      const _fronteggLoaded = (Vue.fronteggPlugins || []).reduce((loaded, plugin) => loaded && !plugin.loading, true);

      if (_fronteggLoaded !== fronteggLoaded) {
        fronteggLoaded = _fronteggLoaded;

        if (isVue3) {
          fronteggLoadedV3.value = _fronteggLoaded;
        }

        for (const subscriber of fronteggLoadedSubscribes) {
          subscriber();
        }
      }
    };

    router === null || router === void 0 ? void 0 : router.getRoutes().map(route => {
      const beforeEnterFn = route.beforeEnter;

      route.beforeEnter = (route, redirect, next) => {
        const callNext = () => {
          if (beforeEnterFn) {
            beforeEnterFn(route, redirect, next);
          } else {
            next();
          }
        };

        if (fronteggLoaded) {
          callNext();
          return;
        }

        const waitFor = setInterval(() => {
          checkIfPluginsLoaded();

          if (fronteggLoaded) {
            clearInterval(waitFor);
            callNext();
          }
        }, 1);
      };
    });
    const waitFor = setInterval(() => {
      checkIfPluginsLoaded();

      if (fronteggLoaded) {
        clearInterval(waitFor);
      }
    }, 10);

    const isAuthRoutes = path => {
      const {
        routes
      } = Vue.fronteggAuth;
      return Object.values(routes).filter(path => path != routes.authenticatedUrl).includes(path);
    };

    const authorizedContentGuard = _this => {
      if (!_this.authorizedContent || isAuthRoutes(_this.$route.path)) {
        return;
      }

      if (!Vue.fronteggAuth.loading && !Vue.fronteggAuth.isAuthenticated) {
        if (options === null || options === void 0 ? void 0 : options.hostedLoginBox) {
          Vue.fronteggAuth.loginActions.requestHostedLoginAuthorize();
        } else {
          const {
            loginUrl
          } = Vue.fronteggAuth.routes;

          _this.$router.push(loginUrl);
        }
      }
    };

    if (!pluginRegistered) {
      registerPlugins(Vue);
    } // should be function not arrow to maintain this


    function loginWithRedirect() {
      // @ts-ignore
      if (!this.$route.path.startsWith(Vue.fronteggAuth.routes.hostedLoginRedirectUrl)) {
        store.dispatch({
          type: 'auth/setState',
          payload: {
            isLoading: true
          }
        });
        Vue.fronteggAuth.loginActions.requestHostedLoginAuthorize();
      }
    }

    if (isVue3) {
      // @ts-ignore - provide will exist only in vue 3 app
      Vue.provide(fronteggLoadedKey, fronteggLoadedV3);
      const {
        authState,
        unsubscribe
      } = connectFronteggStoreV3(store);
      const fronteggAuthReactive = reactive(Object.assign({}, Vue.fronteggAuth)); // @ts-ignore

      Vue.provide(authStateKey, authState); // @ts-ignore

      Vue.provide(unsubscribeFronteggStoreKey, unsubscribe); // @ts-ignore

      Vue.provide(fronteggAuthKey, fronteggAuthReactive); // @ts-ignore

      Vue.provide(routerKey, router); // @ts-ignore

      Vue.provide(fronteggOptionsKey, options); // @ts-ignore

      Vue.provide(fronteggStoreKey, store);
    }

    Vue.mixin({
      data: () => ({
        fronteggLoaded
      }),

      beforeCreate() {
        setStoreKey(this, store);
        this.fronteggAuth = Vue.fronteggAuth;
        this.loginWithRedirect = loginWithRedirect.bind(this); // _entitlements was added for to make the computed property reactive, then it will get updated

        this.getEntitlements = (_entitlements, keys) => fronteggApp.getEntitlements(keys);

        connectMapState(this);
      },

      updated() {
        authorizedContentGuard(this);
      },

      mounted() {
        authorizedContentGuard(this);
      },

      created() {
        if (getStoreBinding(this)) {
          setStoreUnsubscribe(this, store.subscribe(syncStateWithComponent(this, getStoreBinding(this))));
        }

        setLoadingUnsubscribe(this, fronteggLoadedSubscribe(this));
      },

      beforeDestroy() {
        storeUnsubscribe(this);
        loadingUnsubscribe(this);
      }

    });
  }

};
/**
 * @deprecated since version 2.0
 * use instead:
 * import {AdminPortal} from '@frontegg/vue'
 * AdminPortal.show()
 */

const openAdminPortal = () => AdminPortal.show();
/**
 * @deprecated since version 2.0
 * use instead:
 * import {AdminPortal} from '@frontegg/vue'
 * AdminPortal.hide()
 */


const closeAdminPortal = () => AdminPortal.hide();

export { Frontegg, closeAdminPortal, isAuthenticatedGuard, mapAcceptInvitationActions, mapActivateAccountActions, mapApiTokensActions, mapAuthActions, mapForgotPasswordActions, mapLoginActions, mapMfaActions, mapProfileActions, mapSecurityPolicyActions, mapSignupActions, mapSocialLoginActions, mapSsoActions, mapTeamActions, openAdminPortal, useAuthState, useEntitlements, useFrontegg, useFronteggAuthGuard, useFronteggLoaded, useUnsubscribeFronteggStore };
